import { defineComponent as _defineComponent } from 'vue'
import { watch, ref, provide } from "vue"
import PersonalInfoFormContainer from "../PersonalInfoForm/PersonalInfoFormContainer.vue"
import { Step } from "../constants"
import FinalStep from "../shared/FinalStep.vue"
import {
  AcceptInvitationMutation,
  AcceptInvitationMutationMutation,
  ConsultantOnboardingInput,
} from "@/gql"
import ExperienceInfoForm from "../ExperienceInfoForm/ExperienceInfoForm.vue"
import EducationInfoForm from "../EducationInfoForm/EducationInfoForm.vue"
import PasswordFormContainer from "../PasswordForm/PasswordFormContainer.vue"

import { useMutation } from "@/hooks/useMutation"
import { useNotification } from "@/hooks/useNotification"
import useRouter from "@/router/useRouter"
import InvitationLayout from "../shared/InvitationLayout.vue"
import CheckStep from "../shared/CheckStep.vue"
import { OnboardingFormValues } from "../shared/types"
import { NewConsultantInput } from "../shared/useNewConsultantInputForm"

type LoginCredentials = {
  email: string
  password: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ConsultantInvitationFlow',
  props: {
    invitationId: null,
    email: null
  },
  setup(__props: any) {

const props = __props as {
  invitationId: string
  email?: string | undefined
}



const step = ref(Step.PersonalInfo)
const stages = [Step.PersonalInfo, Step.Experience, Step.Education, Step.Password, Step.Final]
const completedSteps = ref<string[]>([])

watch(step, () => (completedSteps.value = [...completedSteps.value, step.value]), {
  immediate: true,
})

const gotoNext = () => {
  const index = stages.findIndex((s) => s === step.value)
  if (index < stages.length - 1) {
    step.value = stages[index + 1]
  }
}

const gotoPrev = () => {
  const index = stages.findIndex((s) => s === step.value)
  if (index > 0) {
    step.value = stages[index - 1]
  }
}

const gotoFinal = () => {
  step.value = Step.Final
}

const isSubmitting = ref(false)
const loginCredentials = ref<LoginCredentials>({ email: "", password: "" })

const { router } = useRouter()

const onboardingFormValues = ref<OnboardingFormValues & { newConsultantInput: NewConsultantInput }>(
  {
    invitationId: props.invitationId,
    token: router.currentRoute.params?.token || "",
    newConsultantInput: {
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: "",
      currentOccupation: "",
      bio: "",
      linkedinUrl: "",
      mbti: null,
      avatar: null,
      residentialAddress: "",
      consultantEducationsAttributes: [],
      consultantWorkExperiencesAttributes: [],
      phone: null,
    },
    newUserInput: {
      password: "",
    },
  }
)

const fields = [
  "avatar",
  "gender",
  "dateOfBirth",
  "currentOccupation",
  "phone",
  "residentialAddress",
  "bio",
  "mbti",
  "linkedinUrl",
]

const newConsultantInput = ref<NewConsultantInput>({
  ...onboardingFormValues.value.newConsultantInput,
})

provide("newConsultantInput", newConsultantInput)

const updateNewConsultantInput = (value: Partial<ConsultantOnboardingInput>) => {
  newConsultantInput.value = { ...newConsultantInput.value, ...value }
}

const { mutate } = useMutation()
const { addError } = useNotification()

const onSubmitted = async () => {
  const { newUserInput } = onboardingFormValues.value

  loginCredentials.value.email = props.email ?? ""
  loginCredentials.value.password = newUserInput.password ?? ""

  const payload = {
    ...onboardingFormValues.value,
    newConsultantInput: {
      email: props.email ?? "",
      ...newConsultantInput.value,
    },
    newUserInput: {
      avatar: newConsultantInput.value.avatar,
      firstName: newConsultantInput.value.firstName,
      lastName: newConsultantInput.value.lastName,
      password: newUserInput.password,
    },
  }

  delete payload.newConsultantInput.avatar

  isSubmitting.value = true

  await mutate<AcceptInvitationMutationMutation>({
    mutation: AcceptInvitationMutation,
    variables: {
      ...payload,
    },
    onSuccess: () => {
      gotoFinal()
    },
    onMutationError: (error) => {
      addError(error.message)
    },
  })

  isSubmitting.value = false
}

const SuccessMessage = {
  heading: "Your profile is live!",
  subtitle: "You're officially a member of our community. Lets find you some work!",
}

const subtitle =
  "Let's start with the basics. Please fill in your personal information to complete your profile. Your details will help us reach you for potential projects."

return { __sfc: true,props, step, stages, completedSteps, gotoNext, gotoPrev, gotoFinal, isSubmitting, loginCredentials, router, onboardingFormValues, fields, newConsultantInput, updateNewConsultantInput, mutate, addError, onSubmitted, SuccessMessage, subtitle, PersonalInfoFormContainer, Step, FinalStep, ExperienceInfoForm, EducationInfoForm, PasswordFormContainer, InvitationLayout, CheckStep }
}

})